import { z } from "zod"
import type { JsonObject, JsonValue } from "../primitives.js"
import type { DomainEventType } from "../taxonomy.js"
import { isSelectableWorkflowTrigger } from "./catalog.js"

/* What the engine reads off a claimed outbox message, and nothing else.
 *
 * The outbox payload is jsonb written by seven different call sites, so every
 * read here is guarded and a payload that does not say what it is about is
 * refused rather than guessed at. Pure, so the interesting cases (a batch with
 * no documents, an event type nobody can build a rule on, a document id that is
 * not a string) are unit tests rather than a queue and a database. */

export type WorkflowTriggerEvent = {
  /** The domain event row, when the payload names one. */
  readonly eventId: string | null
  readonly eventType: DomainEventType
  /**
   * One run per document. A batch is the only trigger that carries more than
   * one, and it fans out: every check and every action is document-scoped, so a
   * batch event that stayed whole would offer checks that cannot be evaluated.
   */
  readonly documentIds: readonly string[]
  /** The posting a `posting_completed` is about, for the run-depth lookup. */
  readonly postingId: string | null
  /** The export an `export_generated` is about, for the same reason. */
  readonly exportId: string | null
}

const nonEmptyEventStringSchema = z.string().trim().min(1)

function readString(source: JsonObject, key: string): string | null {
  return nonEmptyEventStringSchema.safeParse(source[key]).data ?? null
}

/**
 * The documents one event is about, in the order the payload listed them.
 *
 * A batch carries `documentIds`; everything else carries a single `documentId`.
 * Duplicates are collapsed, because the idempotency key is per document and a
 * repeated id would mean one insert and one silent no-op rather than two runs.
 */
function readDocumentIds(source: JsonObject): readonly string[] {
  const list = source.documentIds

  if (Array.isArray(list)) {
    return [
      ...new Set(
        list.flatMap((entry) => {
          const parsed = nonEmptyEventStringSchema.safeParse(entry)
          return parsed.success ? [parsed.data] : []
        })
      ),
    ]
  }

  const single = readString(source, "documentId")

  return single ? [single] : []
}

/**
 * Reads a claimed workflow outbox message, or returns null when it does not
 * describe something a rule could run on.
 *
 * Null is a settled message rather than a failure: an event nobody can build a
 * rule on is not an error, it is an event with no rules, and retrying it
 * forever would be the same answer at a cost.
 */
export function parseWorkflowTriggerEvent(
  payload: JsonValue | undefined
): WorkflowTriggerEvent | null {
  if (
    payload === null ||
    Array.isArray(payload) ||
    Object.prototype.toString.call(payload) !== "[object Object]"
  ) {
    return null
  }

  // SAFETY: JsonValue has only one non-array object member, JsonObject, and the
  // guards above excluded null and arrays before the object is read.
  const source = payload as JsonObject
  const eventType = readString(source, "eventType")

  // Only the six a customer can choose. The two hidden housekeeping events are
  // refused here as well as in the editor, so an outbox row written for one
  // cannot become a run through some later widening of the trigger catalogue.
  if (!eventType || !isSelectableWorkflowTrigger(eventType)) {
    return null
  }

  return {
    eventId: readString(source, "eventId"),
    eventType,
    documentIds: readDocumentIds(source),
    postingId: readString(source, "postingId"),
    exportId: readString(source, "exportId"),
  }
}

/**
 * The once-per-event promise, written down.
 *
 * A batch fans out to one run per document, so the key carries both: the event
 * alone would let one rule run once for a forty document batch, and the
 * document alone would let a second event about the same document be dropped.
 * Unique on workflow plus this, which is what makes a replayed event, a double
 * dequeue and a retried run produce one posting rather than three.
 */
export function workflowRunIdempotencyKey(input: {
  readonly eventId: string | null
  readonly documentId: string | null
  /** Falls back to the outbox row when the payload named no domain event. */
  readonly outboxMessageId: string
}): string {
  return `${input.eventId ?? `outbox:${input.outboxMessageId}`}:${input.documentId ?? "none"}`
}
